import type { BBox } from "@/lib/sketch-geometry"
import { cn } from "@/lib/utils"

export type SketchTextStyleValue = {
  fontSize: number
  fill: string
  bold: boolean
  italic: boolean
  underline: boolean
}

type Props = {
  open: boolean
  /** Kept for callers; both modes use the same flat Bold + Color bar. */
  mode?: "text" | "arrowLabel"
  anchor: BBox
  value: SketchTextStyleValue
  onChange: (next: SketchTextStyleValue) => void
  labels: {
    style: string
    size: string
    fill: string
  }
  /** Camera zoom; bar stays screen-sized via 1/zoom. */
  zoom?: number
}

const STYLE_GAP_Y_PX = 8

const CHIP =
  "inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-ui-border bg-ui-bg-elevated px-2 text-xs font-semibold text-ui-text-secondary shadow-sm hover:bg-ui-surface-raised hover:text-ui-text-primary"

/**
 * Floating style bar above text / arrow-label: Bold toggle + color swatch (native picker).
 */
export function SketchTextPanel({ open, anchor, value, onChange, labels, zoom = 1 }: Props) {
  if (!open) return null

  const inv = zoom > 0 ? 1 / zoom : 1
  const left = Math.max(0, anchor.x + anchor.w / 2)
  const top = Math.max(0, anchor.y - STYLE_GAP_Y_PX * inv)
  const fillHex = value.fill.startsWith("#") ? value.fill.slice(0, 7) : "#111827"

  return (
    <div
      data-sketch-text-style=""
      className="pointer-events-auto absolute z-40 flex gap-1 rounded-lg border border-ui-border bg-ui-bg-elevated p-1 shadow-xl"
      style={{
        left,
        top,
        transform: `translate(-50%, -100%) scale(${inv})`,
        transformOrigin: "center bottom",
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        title={labels.style}
        className={cn(CHIP, value.bold && "border-ui-accent bg-ui-accent/15 text-ui-text-primary")}
        onClick={() => onChange({ ...value, bold: !value.bold })}
      >
        B
      </button>
      <label title={labels.fill} className={cn(CHIP, "relative cursor-pointer p-1.5")}>
        <span
          aria-hidden
          className="pointer-events-none block h-4 w-4 rounded-full border border-black/25 dark:border-white/25"
          style={{ backgroundColor: fillHex }}
        />
        <input
          type="color"
          value={fillHex}
          className="absolute inset-0 cursor-pointer opacity-0"
          onChange={(e) => onChange({ ...value, fill: e.target.value })}
        />
      </label>
    </div>
  )
}
